import React, { useState, useContext, useEffect, useRef } from "react";
import { withStyles } from "@material-ui/core";
import { FormattedMessage, injectIntl } from "react-intl";
import ErrorAlert from "~/Components/ErrorAlert";
import { UserContext } from "~/Services/Providers";
import UnifiedResetPasswordForm from "./Partials/UnifiedResetPasswordForm";
import VerificationCodeForm from "./Partials/VerificationCodeForm";
import AddNewPassword from "./Partials/AddNewPassword";
import useStyles from "./styles";
import StatusField from "~/Components/From/StatusField";
import { postMessageHeight, postMessageScrollUp } from "~/Services/Helpers";

// delay to allow screen readers to finish reading header before focusing an input
const INTRO_TO_INPUT_DELAY_MS = 600;

function ResetPassword({ classes, setValue, entityStore, isDisabled, intl }) {
  const { userContextObj } = useContext(UserContext);
  const [step, setStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [showHeader, setShowHeader] = useState(true);
  const [loading, setLoading] = useState(false);
  const [pageTitle, setPageTitle] = useState("");
  const rootRef = useRef(null); 

  useEffect(() => {
    postMessageScrollUp();

    const t = setTimeout(() => {
      setPageTitle(
        <>
          <FormattedMessage id="ResetYourPassword" /> "! "
          <FormattedMessage id="resetPasswordDescription" />
          "! "
          <FormattedMessage id="chooseAMethod" />
        </>
      );
    }, INTRO_TO_INPUT_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (rootRef.current) {
      postMessageHeight(rootRef.current);
    }
  }, [step, errorMessage, showHeader, pageTitle]);
  

  const moveForward = (formData = {}) => {
    if (formData.email && !formData.cell) {
      setValue(1, "resetEmailPasswordSuccessMessage", "success");
    } else {
      setStep((prev) => prev + 1);
    }
  };

  const moveFinish = (data = {}) => {
    setValue(1, "resetPasswordSuccessMessage", "success");
  };

  let stepComponent = null;
  if (step === 0) {
    stepComponent = (
      <UnifiedResetPasswordForm
        entityStore={entityStore}
        userContextObj={userContextObj}
        moveForward={moveForward}
        setLoading={setLoading}
        loading={loading}
        setErrorMessage={setErrorMessage}
      />
    );
  } else if (step === 1) {
    stepComponent = (
      <VerificationCodeForm
        userContextObj={userContextObj}
        removeHeader={() => setShowHeader(false)}
        moveForward={moveForward}
        setLoading={setLoading}
        setErrorMessage={setErrorMessage}
      />
    );
  } else if (step === 2) {
    stepComponent = (
      <AddNewPassword
        userContextObj={userContextObj}
        removeHeader={() => setShowHeader(false)}
        moveForward={moveFinish}
        setLoading={setLoading}
        setErrorMessage={setErrorMessage}
      />
    );
  }

  return (
    <div className={classes.root} ref={rootRef}>
      {errorMessage && (
        <div aria-live="polite" id="reset-method-error" role="alert">
          <ErrorAlert messageId={errorMessage} />
        </div>
      )}

      {showHeader && (
        <>
          <StatusField>{pageTitle}</StatusField>
          <div
            className={classes.header}
            role="region"
            aria-describedby="reset-desc choose-method"
          >
            <h1 id="reset-password-title" className={classes.title}>
              <FormattedMessage id="ResetYourPassword" />
            </h1>
            <p id="reset-desc">
              <FormattedMessage id="resetPasswordDescription" />
            </p>
            <p className={classes.subheader} id="choose-method">
              <FormattedMessage
                id="chooseAMethod"
                defaultMessage="chooseAMethod"
              />
            </p>
          </div>
        </>
      )}
      {stepComponent}
    </div>
  );
}

export default withStyles(useStyles)(injectIntl(ResetPassword));
